import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";

import { AuthProvider } from "@/lib/auth";
import Header from "@/components/Header";
import HomePage from "@/pages/HomePage";
import { LoginPage, RegisterPage } from "@/pages/AuthPages";
import TradespeopleDirectoryPage from "@/pages/TradespeopleDirectoryPage";
import TradespersonProfilePage from "@/pages/TradespersonProfilePage";
import JobsBoardPage from "@/pages/JobsBoardPage";
import JobDetailPage from "@/pages/JobDetailPage";
import PostJobPage from "@/pages/PostJobPage";
import DashboardPage from "@/pages/DashboardPage";
import TermsPage from "@/pages/TermsPage";
import ProfileEditPage from "@/pages/ProfileEditPage";
import AuthCallback from "@/pages/AuthCallback";
import ClaimPage from "@/pages/ClaimPage";

function Footer() {
  return (
    <footer className="border-t border-slate-200 bg-white mt-16" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 text-sm text-slate-500 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col gap-1">
          <span>© {new Date().getFullYear()} Fixus — Built for Canadian homes.</span>
          <span className="text-xs text-slate-400" data-testid="footer-friendsoft">
            Product built by <span className="font-medium text-slate-600">FriendSoft Corp</span>
          </span>
        </div>
        <div className="flex gap-5 flex-wrap">
          <a href="/tradespeople" className="hover:text-slate-900">Find a Pro</a>
          <a href="/jobs" className="hover:text-slate-900">Browse Jobs</a>
          <a href="/post-job" className="hover:text-slate-900">Post a Job</a>
          <a href="/terms" className="hover:text-slate-900">Terms</a>
        </div>
      </div>
    </footer>
  );
}

function AppRouter() {
  const location = useLocation();
  // CRITICAL: detect Emergent Auth callback synchronously (URL fragment).
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/tradespeople" element={<TradespeopleDirectoryPage />} />
      <Route path="/tradespeople/:id" element={<TradespersonProfilePage />} />
      <Route path="/jobs" element={<JobsBoardPage />} />
      <Route path="/jobs/:id" element={<JobDetailPage />} />
      <Route path="/post-job" element={<PostJobPage />} />
      <Route path="/dashboard" element={<DashboardPage />} />
      <Route path="/profile/edit" element={<ProfileEditPage />} />
      <Route path="/claim/:token" element={<ClaimPage />} />
      <Route path="/terms" element={<TermsPage />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <Helmet>
            <link rel="canonical" href={typeof window !== "undefined" ? window.location.origin + window.location.pathname : "/"} />
            <meta property="og:site_name" content="Fixus" />
            <meta property="og:type" content="website" />
            <meta name="twitter:card" content="summary_large_image" />
          </Helmet>
          <div className="App min-h-screen flex flex-col">
            <Header />
            <main className="flex-1">
              <AppRouter />
            </main>
            <Footer />
          </div>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
